import palettes from './siteThemePalettes.json';

export const SITE_THEME_IDS = ['light', 'gray', 'dark'] as const;
export type SiteThemeId = typeof SITE_THEME_IDS[number];
export const SITE_THEME_CONTROL_ORDER = ['dark', 'gray', 'light'] as const;
export const SITE_THEME_LABELS: Record<SiteThemeId, string> = { light: 'День', gray: 'Рассвет', dark: 'Ночь' };
export const SITE_THEME_GUEST_KEY = 'lisad.site-theme.guest.v1';
export type SiteThemeTokens = typeof palettes.light;
export const THEME_OVERRIDE_KEYS = ['accent', 'accentSecondary', 'surfaceTint', 'headerTint', 'iconPlateTint', 'particleColor'] as const;
export type ThemeOverrides = Partial<Record<typeof THEME_OVERRIDE_KEYS[number], string>>;
export type ThemeRevision = { revision: number; overrides: ThemeOverrides };
export type SiteThemes = { schema_version: 1; themes: Record<SiteThemeId, ThemeRevision> };

export function normalizeSiteTheme(value: unknown): SiteThemeId {
  return value === 'gray' || value === 'dark' ? value : 'light';
}

export function rgb(hex: string): number[] {
  return [1, 3, 5].map((start) => parseInt(hex.slice(start, start + 2), 16));
}

export function contrast(a: string, b: string): number {
  const luminance = (hex: string) => rgb(hex).map((value) => {
    const channel = value / 255;
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  }).reduce((sum, value, index) => sum + value * [0.2126, 0.7152, 0.0722][index], 0);
  const [x, y] = [luminance(a), luminance(b)];
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

export function mixColor(base: string, tint: string, ratio: number): string {
  const target = rgb(tint);
  return '#' + rgb(base).map((value, index) => Math.round(value * (1 - ratio) + target[index] * ratio).toString(16).padStart(2, '0')).join('').toUpperCase();
}

export function resolveSiteTheme(theme: SiteThemeId, overrides: ThemeOverrides = {}): SiteThemeTokens {
  const tokens: SiteThemeTokens = { ...palettes[theme] };
  for (const key of ['accent', 'accentSecondary', 'particleColor'] as const) {
    if (overrides[key]) tokens[key] = overrides[key];
  }
  if (overrides.surfaceTint) {
    for (const key of ['surface', 'raised', 'inset'] as const) tokens[key] = mixColor(tokens[key], overrides.surfaceTint, 0.06);
  }
  if (overrides.headerTint) tokens.header = mixColor(tokens.header, overrides.headerTint, 0.08);
  if (overrides.iconPlateTint) {
    tokens.iconPlateTop = mixColor(tokens.iconPlateTop, overrides.iconPlateTint, 0.10);
    tokens.iconPlateBottom = mixColor(tokens.iconPlateBottom, overrides.iconPlateTint, 0.10);
  }
  return tokens;
}

export function validateThemeOverrides(theme: SiteThemeId, value: unknown): string | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return 'Ожидается объект цветов';
  for (const [key, color] of Object.entries(value)) {
    if (!(THEME_OVERRIDE_KEYS as readonly string[]).includes(key)) return `Недоступный параметр: ${key}`;
    if (typeof color !== 'string' || !/^#[0-9a-f]{6}$/i.test(color)) return 'Цвет должен иметь формат #RRGGBB';
  }
  const tokens = resolveSiteTheme(theme, value as ThemeOverrides);
  for (const background of [tokens.bg, tokens.surface, tokens.raised, tokens.inset, tokens.header]) {
    for (const key of ['text', 'secondary', 'muted', 'accent', 'accentSecondary', 'success', 'warning', 'danger'] as const) {
      if (contrast(tokens[key], background) < 4.5) return 'Недостаточный контраст текста. Выберите другой оттенок';
    }
    if (contrast(tokens.borderControl, background) < 3) return 'Недостаточный контраст границы поля';
  }
  for (const key of ['accent', 'accentSecondary', 'success', 'warning', 'danger'] as const) {
    if (contrast(tokens.onAccent, tokens[key]) < 4.5) return 'Недостаточный контраст текста кнопки';
  }
  return null;
}

export function normalizeSiteThemes(value: unknown): SiteThemes {
  const source = value as Partial<SiteThemes> | null;
  const themes = Object.fromEntries(SITE_THEME_IDS.map((id) => {
    const entry = source?.schema_version === 1 ? source.themes?.[id] : null;
    const valid = entry && Number.isSafeInteger(entry.revision) && entry.revision >= 0 && !validateThemeOverrides(id, entry.overrides);
    return [id, valid ? { revision: entry.revision, overrides: entry.overrides } : { revision: 0, overrides: {} }];
  })) as Record<SiteThemeId, ThemeRevision>;
  return { schema_version: 1, themes };
}

export function themeVariables(tokens: SiteThemeTokens): Record<string, string> {
  // Роли интерфейса вычисляются и для админских палитр, не меняя сохранённые настройки.
  const presentation = {
    action: mixColor(tokens.secondary, tokens.accent, 0.25),
    selected: mixColor(tokens.surface, tokens.secondary, 0.14),
    onSelected: tokens.text,
    selectedBorder: tokens.secondary,
    progress: tokens.borderControl,
    grid: mixColor(tokens.border, tokens.borderControl, 0.65),
  };
  return Object.fromEntries(Object.entries({ ...tokens, ...presentation }).flatMap(([key, value]) => {
    const name = '--site-' + key.replace(/[A-Z]/g, (letter) => '-' + letter.toLowerCase());
    return [[name, value], [name + '-rgb', rgb(value).join(' ')]];
  }));
}

export function themeStyleSheet(): string {
  return SITE_THEME_IDS.map((theme) => `${theme === 'light' ? ':root,' : ''}[data-site-theme="${theme}"]{color-scheme:${theme === 'light' ? 'light' : 'dark'};${Object.entries(themeVariables(resolveSiteTheme(theme))).map(([key, value]) => `${key}:${value}`).join(';')}}`).join('\n');
}

// Только выбор оформления: не сериализуем пользовательские данные в HTML.
export function themeBootstrap(): string {
  return `(()=>{const normalize=${normalizeSiteTheme.toString()};let value='light';try{const s=localStorage.getItem('access')?localStorage:sessionStorage;const raw=s.getItem('access')?s.getItem('user'):null;value=raw?JSON.parse(raw)?.preferences?.site_theme:localStorage.getItem('${SITE_THEME_GUEST_KEY}');}catch{}const theme=normalize(value);document.documentElement.dataset.siteTheme=theme;document.documentElement.classList.toggle('dark',theme!=='light');})();`;
}
